/**
 * CÓMO VA UN MACRO EN EL INICIO: la regla de color, en un solo sitio.
 *
 * Del artifact del 25-08 (puntos 78, 79, 82 y 83), y la frase que lo resume es suya:
 * «verde si ese macro ya está resuelto · naranja si te has pasado · sin color mientras
 * vas por debajo. Igual en las cuatro pestañas, sin excepciones».
 *
 * Antes cada pestaña se lo apañaba por su cuenta dentro del JSX: solo Falta pintaba algo,
 * solo cuando el número salía negativo, y no había margen ninguno -- un gramo de desvío ya
 * cambiaba el color. Ahora las cuatro preguntan aquí.
 *
 * EL MARGEN SON 4 g, PLANOS. Se usa `MARGEN` de `lib/exceso`, que es el de Calma y el que
 * ya aplican las comidas, pero NO su `margenDe()`: aquel estrecha el margen cuando lo que
 * se pide es poco (9 g de proteína en el intra) y aquí hablamos siempre de los totales del
 * día, que son cientos de gramos. «De 1 a 4, falte o sobre, es válido y sale en verde. A
 * partir de 5, naranja. No hace falta cuadrar al gramo.»
 *
 * NARANJA SOLO PARA LO QUE SE PASA. Del 26-08 al 27-08 esto pintó naranja también lo que
 * faltaba -- decisión de Francisco, «a partir de 5, falte o sobre» --, y el 27-08 vuelve a la
 * frase de arriba: manda la maqueta de la parte 6, que dibuja un día a medias y lo remata con
 * «ni un color en toda la pantalla, porque no hay nada mal, sólo cosas por hacer».
 *
 * Y EL 13-09 VUELVE A CAMBIAR, que es la cuarta vuelta de esta misma línea: su punto 02 del
 * 11-09 pide «verde si está válido, naranja si falta, rojo si se pasa». Manda lo más
 * reciente. Para que quepan los tres, pasarse se va al ROJO de verdad: el `pasado` de antes
 * era el naranja de la casa, y con el naranja puesto en «falta» las dos situaciones
 * contrarias habrían quedado del mismo color.
 *
 * Lo que distingue faltar de sobrar sigue siendo la LONGITUD de la barra: la del que falta
 * está corta y la del que sobra está llena a tope (punto 83). Ahora, además, el color.
 */
import { MARGEN, seExcede } from './exceso';
import { num0, num1, alDecima } from './numeros';

// EL SUELO DEL «CUADRADO»: MEDIO GRAMO, EL DE CALMA (Francisco, 3-09-2026: «tal cual lo
// hace Calma»). No es una elección nuestra: está en su bundle, en `macros.stepRedondeo = 0.5`,
// y en la función que pinta cada fila -- `Math.round(objetivo − servido) == 0` saca
// «Cuadrado», o sea por debajo de medio gramo; hasta `margenValido = 4`, «Válido»; más allá,
// «faltan» o «sobran». Es la misma escalera que la de abajo.
//
// Estaba en 1 g, que venía de cuando el día iba en enteros y sólo la comida llevaba decimal.
// Con el decimal puesto en todas partes ese suelo SE VE: «249,2 de 250» rotulado «cuadrado»
// vuelve a ser un número que no cuadra con su palabra. Con medio gramo eso se lee
// «válido −0,8», que es lo que el cliente tiene delante.
//
// Y desde el 5-09 lo que cae DENTRO del suelo tampoco se calla su décima: «cuadrado +0,1»
// (ficha 12; ver `palabraCuadrado`). El suelo no se mueve, sólo deja de esconder el número.
export const SUELO_CUADRADO = 0.5;

/**
 * LOS CUATRO COLORES, en un solo sitio para que las dos pantallas que leen esto pinten igual.
 *
 *   'ok'        verde     ese macro ya está resuelto
 *   'pasado'    naranja   te has pasado
 *   null        blanco    vas por debajo: no es un error, es que no has terminado
 *   'apagado'   gris      no es un estado (el rótulo «tu objetivo» de la pestaña Macros)
 */
export const claseDelMacro = (color) => (
    color === 'ok' ? 'text-ok font-medium'
    : color === 'pasado' ? 'text-pasado font-medium'
    : color === 'sepasa' ? 'text-sepasa font-medium'
    : color === 'falta' ? 'text-falta font-medium'
    : color === 'apagado' ? 'text-muted-foreground'
    : 'text-foreground');

//: El relleno de la barra. Por debajo va en gris: la barra dice cuánto llevas, no si vas mal.
export const fondoDelMacro = (color) => (
    color === 'ok' ? 'bg-ok'
    : color === 'pasado' ? 'bg-pasado'
    : color === 'sepasa' ? 'bg-sepasa'
    // La barra de lo que falta, en el naranja pero más floja: lo que dice cuánto llevas es
    // su LONGITUD, y a todo color competía con el rojo del que se pasa.
    : color === 'falta' ? 'bg-falta/70'
    : 'bg-muted-foreground/40');

//: Y el punto solo lo llevan los dos que son un estado de verdad.
export const llevaPunto = (color) => (
    color === 'ok' || color === 'pasado' || color === 'sepasa' || color === 'falta');

export const SIN_ESTADO = 'sin_estado';
export const CORTO = 'corto';
export const VALIDO = 'valido';
export const CLAVADO = 'clavado';
export const PASADO = 'pasado';

// El signo de «válido +3» / «válido −4» es el MENOS de verdad (U+2212), no un guion: al
// lado de un número, el guion se lee como parte del texto y el menos se lee como signo.
const MENOS = '−';

/**
 * «CUADRADO» ENSEÑA EL DECIMAL (Jesús, ficha 12 del 5-09-2026, y lo dijo el 4 con Francisco
 * delante: «cuadrado es cuando es un decimal y se pone con el signo positivo o negativo
 * correspondiente»).
 *
 * Con 60,1 de grasa sobre 60 la palabra salía «cuadrado» a secas, sin número, y al lado se
 * leía el 60,1: la pantalla decía a la vez que cuadra y que no. Ahora lo clavado de verdad
 * sigue siendo «cuadrado» a secas -- no hay nada que enseñar -- y lo que baila una décima o
 * dos lo dice: «cuadrado +0,1», «cuadrado −0,3».
 *
 * NO ES VOLVER AL «CUADRADO (−1,7)» DEL 2-09, que él dio por muerto esta misma mañana. Ahí
 * la palabra se estiraba hasta los 4 g del margen y tapaba el «válido»; aquí sólo llega al
 * suelo, o sea a ±0,4 como mucho, y de 0,5 en adelante manda «válido» igual que hasta ahora.
 * Y sin paréntesis, tal como él lo escribe. Desde el 6-09 «válido» tampoco lo lleva:
 * Jesús lo mandó quitar ese día, así que ya no queda ninguno en la serie.
 *
 * EL DESVÍO SE MIDE A LA DÉCIMA, la que se ve. Con el exacto, un 60,04 sobre 60 escribiría
 * «cuadrado +0» -- un signo delante de un cero --, y el número que el cliente tiene delante
 * es 60 sobre 60: ahí no hay decimal que enseñar y va a secas.
 */
const palabraCuadrado = (desvio, n) => {
    const visible = alDecima(desvio);
    if (!visible) return 'cuadrado';
    // Y SI CON EL REDONDEO DE ESA PANTALLA EL DESVÍO NO SE VE, TAMPOCO SE ESCRIBE. En
    // Inicio los números van redondos desde el 16-09, y ahí un +0,4 se escribiría
    // «cuadrado +0»: un signo delante de un cero, que no dice nada. A la décima esto no
    // pasa nunca, porque el suelo del cuadrado es medio gramo.
    const escrito = n(Math.abs(visible));
    if (!parseFloat(String(escrito).replace(',', '.'))) return 'cuadrado';
    return `cuadrado ${visible > 0 ? '+' : MENOS}${escrito} g`;
};

// CON SU UNIDAD (Jesús, 26-09: «no pone qué son esos números, si gramos, calorías... para ti es
// obvio, pero para una persona que no sepa mucho no; abajo de los números dice "faltan", ahí puede
// agregarle la g»). Toda palabra con cifra lleva « g», pegada al número con un espacio que no se
// parte (26-09: a 290 px la «g» bajaba sola a otra línea): faltan, sobran, válido, cuadrado, te pasas y
// el «de 250» de referencia.
const PALABRA = {
    // Macros no lleva estado NUNCA: ahí el número es el objetivo, no hay bueno ni malo.
    macros: () => 'tu objetivo',
    // «CUADRADO» ES CUANDO ESTÁ EXACTO (Francisco, 3-09-2026). Esto REVIERTE la revisión
    // del 2-09, que dentro del margen ponía «cuadrado (+2,6)»: «dice cuadrado pero le
    // faltan 2,3; cuadrado es cuando está exacto, si no es válido, o sobra o falta».
    // Es además la letra del punto 11.1 de Jesús: «de 1 a 4, falte o sobre, es VÁLIDO y
    // sale en verde». Así que: exacto -> «cuadrado»; dentro del margen -> «válido» con su
    // desvío, en verde igual; fuera -> «faltan»/«sobran».
    // Y DESDE EL 5-09 (ficha 12) el cuadrado que no es exacto enseña su décima: «cuadrado
    // +0,1». Ver `palabraCuadrado`, ahí está el porqué.
    //
    // SIN PARÉNTESIS (Jesús, 6-09-2026): «dice una comida válido (1,4), quita el
    // paréntesis». Queda «válido +1,4», que es como ya lo escribía la tarjeta plegada de la
    // comida (`MealCard`) y como se escribe aquí mismo «cuadrado +0,1»: era el último
    // paréntesis que quedaba en toda la serie de estados.
    dieta: (estado, desvio, objetivo, n) => {
        if (estado === CLAVADO) return palabraCuadrado(desvio, n);
        if (estado === VALIDO) return `válido ${desvio > 0 ? '+' : MENOS}${n(Math.abs(desvio))} g`;
        if (estado === PASADO) return `sobran ${n(Math.abs(desvio))} g`;
        return `faltan ${n(Math.abs(desvio))} g`;
    },
    llevas: (estado, desvio, objetivo, n) => {
        // «SOBRAN», NO «TE PASAS» (Francisco, 13-09). Es la palabra que usa el resto de la
        // app para lo mismo -- «faltan 16 · sobran 12», de su lista de siempre -- y aquí se
        // había colado otra: la misma situación dicha de dos maneras según la pestaña.
        if (estado === PASADO) return `sobran ${n(Math.abs(desvio))} g`;
        // «Ya lo tienes» le dice que puede olvidarse de ése el resto del día, que es lo
        // único que necesita saber en esta pestaña.
        if (estado === CLAVADO || estado === VALIDO) return 'ya lo tienes';
        // Y POR DEBAJO, NADA: el «de 250» ya no vive aquí, vive en `referencia` y se pinta
        // SIEMPRE (ver abajo). Antes esta palabra hacía las dos cosas y por eso el objetivo
        // desaparecía justo cuando aparecía el estado.
        return null;
    },
    falta: (estado, desvio, objetivo, n) => {
        if (estado === PASADO) return `te pasas ${n(Math.abs(desvio))} g`;
        // La misma regla del 3-09: «cuadrado» solo el exacto; el del margen, «válido».
        // AQUÍ NO VA LA DÉCIMA DE LA FICHA 12 (5-09), y no es un olvido: en esta pestaña el
        // número que se lee YA ES el desvío (0,1), así que «cuadrado +0,1» lo diría dos
        // veces. Por eso «válido» tampoco lleva el suyo desde que existe esta vista.
        if (estado === CLAVADO) return 'cuadrado';
        if (estado === VALIDO) return 'válido';
        // El número YA es lo que falta, así que no se pone «faltan»: sería decirlo dos veces.
        return 'para llegar';
    },
};
// Dentro de una comida las palabras son las de Dieta -- «cuadrado», «válido +2,3»,
// «faltan 4,5» --, lo que cambia es que llevan decimal.
PALABRA.comida = PALABRA.dieta;

/**
 * Lee un macro y devuelve todo lo que hay que pintar de él.
 *
 * @param vista     'macros' | 'dieta' | 'llevas' | 'falta'
 * @param hay       lo que hay YA (lo creado en Dieta, lo comido en Llevas y en Falta)
 * @param objetivo  el total del día para ese macro
 *
 * Los dos entran A LA DÉCIMA, y la palabra se calcula con esos mismos: la regla de siempre
 * («quien escriba una diferencia al lado de dos cifras redondeadas tiene que restar ESAS»),
 * sólo que desde el 3-09 la cifra que se lee lleva decimal en las cuatro pestañas y no sólo
 * dentro de la comida. La palabra tiene que salir de lo que el cliente está leyendo.
 */
// Y POR DEFECTO, LA REGLA DE SIEMPRE (Francisco, 13-09, segunda vuelta el mismo día: «los
// de Nutrición también revíertelo, el falta sale con un naranja raro que antes no era
// así»). Los tres colores son de UNA pantalla, «Nutrición · añadir ingrediente», que es la
// que nombra su punto 02 del 11-09; el resto de la app -- Inicio, la cabecera del día y las
// comidas -- lleva desde el 27-08 con la suya: lo que falta en blanco, porque no hay nada
// mal, sólo cosas por hacer. Quien quiera los tres colores los pide con `tresColores`.
export function leerMacro({ vista, hay, objetivo, margen, tresColores = false, redondo = false }) {
    if (vista === 'macros') {
        // `apagado` y no `null`: en esta pestaña el número ES el objetivo, así que «tu
        // objetivo» es un rótulo y va en gris. `null` significa otra cosa -- «vas por
        // debajo» --, y eso se pinta en blanco.
        // `referencia: null` explícito y no ausente: quien pinta pregunta por ese campo en
        // las cuatro pestañas, y una forma que a veces trae la clave y a veces no es una
        // trampa esperando. Aquí no hay «de N» porque el número YA es el objetivo.
        return { estado: SIN_ESTADO, palabra: PALABRA.macros(), color: 'apagado',
                 referencia: null, barra: null };
    }
    // EL DECIMAL, TAMBIÉN EN EL GLOBAL (Francisco, 3-09-2026): «que muestre también en el
    // global, así no hay desfase, tal cual lo hace Calma».
    //
    // Esto REVIERTE el punto 80 del 07-08 («ni un decimal en Inicio, ni arriba ni en las
    // comidas»), y con motivo. Con el día redondeado y la comida a la décima, la MISMA comida
    // decía tres cosas a la vez: la fila de Inicio «16G», la tarjeta abierta «15,7» y la
    // plegada «válido +1». Tres cifras del mismo gramo, y el cliente no tiene forma de saber
    // cuál es la buena. Vale más un decimal de más que tres números que no cuadran.
    //
    // Y con el decimal hace falta el mismo SUELO en todas partes, o el día no diría
    // «cuadrado» casi nunca: 249,7 sobre 250 no es un fallo, es que no se puede pesar la
    // diferencia. Así que la regla del gramo, que era de la comida, pasa a ser de la casa.
    // REDONDO EN INICIO, A LA DÉCIMA DENTRO DE LA COMIDA (Francisco, 16-09: «en Inicio a
    // los macros haz que queden redondeados, 192,2 queda en 192 y 192,6 en 193»).
    //
    // Esto acota la regla del 3-09 («que muestre también en el global, tal cual lo hace
    // Calma»), no la tira: la décima sigue mandando DONDE SE AFINA, que es dentro de la
    // comida, y el desfase que aquella decisión arreglaba no vuelve, porque el número de
    // Inicio y el de la comida ya no compiten: uno es el resumen del día y el otro el
    // gramo que se está moviendo. `num0` redondea como se espera: 192,2 a 192 y 192,6 a 193.
    const n = redondo ? num0 : num1;
    const meta = objetivo || 0;
    const tiene = hay || 0;
    // Lo que hay MENOS lo que debería haber: negativo es que falta, positivo es que sobra.
    const desvio = tiene - meta;
    const fuera = Math.abs(desvio);
    // El margen puede venir dado; desde el 3-09 (decisión de Francisco: «1-4 plano en
    // todo») `margenDe` devuelve siempre los 4 de Calma, así que dado o no, es el mismo.
    const tope = margen != null ? margen : MARGEN;

    let estado;
    // Clavado: por debajo de medio gramo, en cualquier pantalla. Es el `Math.round(l) == 0`
    // de Calma, y con el decimal delante es lo más ajustado que se puede decir sin mentir.
    if (fuera < SUELO_CUADRADO) estado = CLAVADO;
    else if (fuera <= tope) estado = VALIDO;
    else if (desvio > 0) estado = PASADO;
    else estado = CORTO;

    const resuelto = estado === CLAVADO || estado === VALIDO;
    // LOS TRES COLORES DEL ESTADO (Francisco, 13-09, sobre su punto 02 del 11-09): «verde si
    // está válido, naranja si falta, rojo si se pasa».
    //
    // Esto REVIERTE lo del 27-08, que dejaba sin color lo que va corto -- «ni un color en
    // toda la pantalla, porque no hay nada mal, sólo cosas por hacer» -- y que a su vez
    // había revertido una decisión suya del 26-08. Tercera vuelta, y manda la más reciente.
    //
    // El número sigue en blanco: el color va solo en la palabra y en la barra, que es la
    // otra mitad de su punto 02.
    // Y `tresColores` DICE DÓNDE, que es la parte que me salté (Francisco, 13-09, el mismo
    // día: «revierte lo de Inicio, eso era algo que cambiamos en un artefacto más
    // reciente»). Su punto 02 habla de UNA pantalla, «Nutrición · añadir ingrediente», y yo
    // lo apliqué a la regla entera, así que se llevó por delante el Inicio, donde manda su
    // maqueta del 27-08: «ni un color en toda la pantalla, porque no hay nada mal, sólo
    // cosas por hacer». Ahí lo que falta va en blanco y lo que se pasa en el naranja de la
    // casa, y así vuelve a estar.
    const color = resuelto ? 'ok'
        : estado === PASADO ? (tresColores ? 'sepasa' : 'pasado')
        : (tresColores ? 'falta' : null);
    return {
        estado,
        desvio,
        palabra: (PALABRA[vista] || PALABRA.dieta)(estado, desvio, meta, n),
        // EL «DE 250» SE VE SIEMPRE, PASE LO QUE PASE (Francisco, 3-09, con la maqueta del
        // bloque 06 delante: «250 / de 250 / ya lo tienes» y «212 / de 210 / cuadrado
        // (+2)»). Son DOS lineas debajo del numero, no una: arriba contra qué se mide y
        // abajo cómo va.
        //
        // Antes el objetivo y el estado se turnaban en la misma línea, así que el objetivo
        // desaparecía justo cuando el estado tenía algo que decir -- que es cuando más falta
        // hace saber de cuánto hablamos.
        //
        // Solo en Dieta y en Llevas. En Macros el número YA es el objetivo, y en Falta el
        // número es lo que queda: repetir el total ahí no dice nada. Y dentro de una comida
        // tampoco, que ahí la fila ya lleva su objetivo al lado.
        referencia: (vista === 'dieta' || vista === 'llevas') ? `de ${n(meta)} g` : null,
        color,
        barra: {
            // La barra hace algo que el punto no hace: en un día cuadrado se llenan las
            // tres de verde de lado a lado. Y su LONGITUD es lo que distingue faltar de
            // sobrar: la del que falta está corta, la del que sobra está llena a tope.
            largo: resuelto || estado === PASADO
                ? 100
                : (meta > 0 ? Math.max(0, Math.min(100, (tiene / meta) * 100)) : 0),
            color,
        },
    };
}

/**
 * LA CELDA DE «NUTRICIÓN · AÑADIR INGREDIENTE», con la palabra de aquí y el color de allí.
 *
 * Francisco, 13-09-2026: «Y falla: "cuadrado" en la tarjeta y "Válido" en el diálogo».
 *
 * Ese diálogo tenía su propia copia de la escalera de estados escrita a mano dentro del
 * componente, y una copia acaba separándose: la de arriba enseña la décima del cuadrado
 * desde el 5-09 («cuadrado −0,4», ficha 12 de Jesús) y aquella decía «cuadrado» a secas.
 * La misma comida leída distinto según la ventana, que es justo lo que la regla prohíbe.
 *
 * La palabra sale de `leerMacro` con `vista: 'comida'`, la misma que pide la tarjeta de la
 * comida, y con los DOS números a la décima como allí: la palabra tiene que salir de lo
 * que el cliente está leyendo.
 *
 * EL COLOR NO ES EL DE LA CASA, y es a propósito: esa pantalla pinta lo que falta en
 * blanco (punto 77: ir corto no es un error, es que no has terminado) y el «sobran» en
 * ROJO sólo cuando de verdad se excede (`seExcede`), que es lo que `leerMacro` no
 * distingue -- para ella todo lo pasado es el naranja de la casa. Y los tonos son los que
 * se leen en claro: a 11 px sobre blanco, `red-500` da 3,3:1 (QA del 15-08).
 *
 * @param key    'P' | 'H' | 'G', para saber si el «sobran» va en rojo
 * @param esPeri en el intra y en el post la grasa no cuenta, así que tampoco se pasa
 */
export function celdaDeLaVentana({ hay, objetivo, key, esPeri = false }) {
    const tiene = alDecima(hay || 0);
    const meta = alDecima(objetivo || 0);
    // CON EL OBJETIVO A CERO NO HAY NADA CUADRADO (Jesús, «El sugeridor del perientreno»,
    // 25-09: la cabecera del intra decía «0 de 0 · cuadrado» con el peri a cero). Mientras no
    // haya nada, se dice que no hay objetivo; si se pone algo, sigue siendo pasarse.
    if (meta <= 0 && tiene <= 0) {
        return { num: num1(tiene), de: 'de 0', palabra: 'sin objetivo', estado: 'sin_objetivo',
                 clase: 'text-muted-foreground', enRojo: false };
    }
    const lectura = leerMacro({ vista: 'comida', hay: tiene, objetivo: meta });
    const enRojo = seExcede(key, tiene, meta, { esPeri });
    const clase = lectura.estado === PASADO
        ? (enRojo ? 'text-red-700 dark:text-red-400' : 'text-muted-foreground')
        : lectura.color === 'ok' ? 'text-green-700 dark:text-green-400'
        : 'text-foreground';
    // «0 de 48», con la palabra y no con la barra: es como lo escribe él y es como se lee
    // en voz alta. El objetivo va debajo del número grande, no pegado.
    return { num: num1(tiene), de: `de ${num1(meta)}`, palabra: lectura.palabra,
             estado: lectura.estado, clase, enRojo };
}
